import React from 'react';
import { useCurrency } from '../../../../hooks/useCurrency';
import { InvoiceData } from '../types';

interface TotalsProps {
  invoice: InvoiceData;
  layout?: 'standard' | 'compact' | 'inline';
  showPaymentInfo?: boolean;
}

export default function Totals({ invoice, layout = 'standard', showPaymentInfo = true }: TotalsProps) {
  const { formatCurrency } = useCurrency();
  if (layout === 'inline') {
    return (
      <div className="flex justify-end mb-4">
        <div className="text-sm space-y-1">
          <div className="flex justify-between gap-8">
            <span>Subtotal:</span>
            <span>{formatCurrency(invoice.subtotal)}</span>
          </div>
          {invoice.discount_amount > 0 && (
            <div className="flex justify-between gap-8 text-red-600">
              <span>Discount:</span>
              <span>-{formatCurrency(invoice.discount_amount)}</span>
            </div>
          )}
          {invoice.tax_amount > 0 && (
            <div className="flex justify-between gap-8">
              <span>Tax:</span>
              <span>{formatCurrency(invoice.tax_amount)}</span>
            </div>
          )}
          <div className="flex justify-between gap-8 font-bold text-lg border-t pt-1">
            <span>Total:</span>
            <span>{formatCurrency(invoice.total)}</span>
          </div>
        </div>
      </div>
    );
  }

  if (layout === 'compact') {
    return (
      <div className="flex justify-end mb-4">
        <div className="w-48 text-xs">
          <div className="flex justify-between py-1 border-b">
            <span>Subtotal:</span>
            <span>{formatCurrency(invoice.subtotal)}</span>
          </div>
          {invoice.discount_amount > 0 && (
            <div className="flex justify-between py-1 border-b text-red-600">
              <span>Discount:</span>
              <span>-{formatCurrency(invoice.discount_amount)}</span>
            </div>
          )}
          {invoice.tax_amount > 0 && (
            <div className="flex justify-between py-1 border-b">
              <span>Tax:</span>
              <span>{formatCurrency(invoice.tax_amount)}</span>
            </div>
          )}
          <div className="flex justify-between py-1 font-bold bg-primary-600 text-white px-2">
            <span>Total:</span>
            <span>{formatCurrency(invoice.total)}</span>
          </div>
          {showPaymentInfo && invoice.amount_paid > 0 && (
            <>
              <div className="flex justify-between py-1 border-b mt-1">
                <span>Paid:</span>
                <span>{formatCurrency(invoice.amount_paid)}</span>
              </div>
              <div className="flex justify-between py-1 font-semibold">
                <span>Balance Due:</span>
                <span>{formatCurrency(invoice.balance_due)}</span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-end mb-6">
      <div className="w-64">
        <div className="flex justify-between py-2 border-b">
          <span className="font-medium">Subtotal:</span>
          <span>{formatCurrency(invoice.subtotal)}</span>
        </div>
        {invoice.discount_amount > 0 && (
          <div className="flex justify-between py-2 border-b text-red-600">
            <span className="font-medium">Discount:</span>
            <span>-{formatCurrency(invoice.discount_amount)}</span>
          </div>
        )}
        {invoice.tax_amount > 0 && (
          <div className="flex justify-between py-2 border-b">
            <span className="font-medium">Tax:</span>
            <span>{formatCurrency(invoice.tax_amount)}</span>
          </div>
        )}
        <div className="flex justify-between py-3 font-bold text-lg bg-primary-600 text-white px-4 mt-2">
          <span>Total Amount:</span>
          <span>{formatCurrency(invoice.total)}</span>
        </div>
        {showPaymentInfo && invoice.amount_paid > 0 && (
          <>
            <div className="flex justify-between py-2 border-b mt-2">
              <span className="font-medium">Amount Paid:</span>
              <span>{formatCurrency(invoice.amount_paid)}</span>
            </div>
            <div className="flex justify-between py-2 font-semibold text-lg">
              <span>Balance Due:</span>
              <span className={invoice.balance_due > 0 ? 'text-red-600' : 'text-green-600'}>
                {formatCurrency(invoice.balance_due)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
